import {
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useTranslation } from "react-i18next";
import { randomId } from "@mui/x-data-grid-generator";

import { AttachmentsModalProps, PendingAttachment } from "./types";
import { FileDropZone } from "./FileDropZone";
import { HyperlinkForm } from "./HyperlinkForm";
import { FileListItem } from "./FileListItem";
import { HyperlinkListItem } from "./HyperlinkListItem";
import ConfirmationPanel from "../confirmationPanel";
import {
  Attachment,
  getAttachments,
  createBlobInContainer,
  createAttachment,
  deleteBlobFromContainer,
  deleteAttachment,
  decorateBlobs,
} from "../../services/attachmentService";
import { Context } from "../context";
import { AppContext } from "../../models/applicationState";
import { UserRole } from "../../models";
import { PaperComponent } from "../../util/paperComponent";

export const AttachmentsModal: FC<AttachmentsModalProps> = ({
  open,
  allowModifications,
  meetingId,
  itemId,
  sessionId,
  taskId,
  onClose,
  onAttachmentsCountChanged,
  uploadPrefix,
  pendingAttachments,
  onPendingAttachmentsChange,
}): ReactElement => {
  const { t } = useTranslation();
  const appContext = useContext<AppContext>(Context);

  const [loadedAttachments, setLoadedAttachments] = useState<Attachment[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [attachmentToDelete, setAttachmentToDelete] =
    useState<Attachment | null>(null);

  // Pending mode: when we have a meetingId but no itemId, and onPendingAttachmentsChange is provided
  // This means we're creating a new item and need to store attachments locally until item is saved
  const isPendingMode = !itemId && !!onPendingAttachmentsChange;

  // Use ref to store callback to avoid infinite loop
  const onAttachmentsCountChangedRef = useRef(onAttachmentsCountChanged);
  onAttachmentsCountChangedRef.current = onAttachmentsCountChanged;
  
  const onPendingAttachmentsChangeRef = useRef(onPendingAttachmentsChange);
  onPendingAttachmentsChangeRef.current = onPendingAttachmentsChange;

  // Track which (open + key) combination has been loaded so we can render the
  // spinner on the very first render after `open` flips, instead of paint →
  // empty → spinner → content.
  const loadKey =
    open && !isPendingMode && (meetingId || itemId || sessionId)
      ? `${meetingId ?? ""}|${itemId ?? ""}|${sessionId ?? ""}|${taskId ?? ""}`
      : null;
  const loadedKeyRef = useRef<string | null>(null);

  // Track whether the Dialog's enter transition has finished. We keep the
  // spinner visible until BOTH the fetch is done AND the animation is done,
  // so fast fetches don't cause a mid-animation content flash.
  const [dialogEntered, setDialogEntered] = useState(false);
  useEffect(() => {
    if (!open) setDialogEntered(false);
  }, [open]);

  // In pending mode, derive the displayed list directly from props — mirroring
  // it into state caused a re-render every time the parent passed a new array
  // reference (visible flicker).
  const attachmentsList = useMemo<Attachment[]>(() => {
    if (isPendingMode) return pendingAttachments?.map((p) => p.attachment) ?? [];
    return loadedAttachments;
  }, [isPendingMode, pendingAttachments, loadedAttachments]);

  // Loading is true whenever we have an outstanding fetch, OR we know we
  // need to fetch but haven't started yet (first render after open=true), OR
  // the Dialog's enter animation hasn't finished yet (prevents content flash
  // when the fetch resolves faster than the ~300ms fade-in).
  const isLoading =
    !dialogEntered ||
    isFetching ||
    (loadKey !== null && loadedKeyRef.current !== loadKey);

  // Load attachments when modal opens (only in normal mode, not pending mode)
  useEffect(() => {
    if (isPendingMode) return;

    if (loadKey && loadedKeyRef.current !== loadKey) {
      let cancelled = false;
      setIsFetching(true);
      getAttachments(meetingId, itemId, sessionId, taskId)
        .then((list) => {
          if (cancelled) return;
          setLoadedAttachments(list || []);
          loadedKeyRef.current = loadKey;
        })
        .catch((error) => {
          if (cancelled) return;
          console.error("Error loading attachments:", error);
          setLoadedAttachments([]);
          loadedKeyRef.current = loadKey;
        })
        .finally(() => {
          if (!cancelled) setIsFetching(false);
        });
      return () => {
        cancelled = true;
      };
    }

    // Reset state when modal closes
    if (!open) {
      setLoadedAttachments([]);
      setIsFetching(false);
      loadedKeyRef.current = null;
    }
  }, [open, meetingId, itemId, sessionId, taskId, isPendingMode, loadKey]);

  // Notify parent of attachment count changes (using ref to avoid infinite loop)
  // Only notify while modal is open — closing resets the list to [] which would
  // falsely report count 0 to the parent and overwrite the real badge number.
  const prevCountRef = useRef<number | null>(null);
  const uploadResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (uploadResetTimeoutRef.current) {
        clearTimeout(uploadResetTimeoutRef.current);
        uploadResetTimeoutRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    if (!open) {
      prevCountRef.current = null;
      return;
    }
    if (
      prevCountRef.current !== null &&
      prevCountRef.current !== attachmentsList.length
    ) {
      onAttachmentsCountChangedRef.current?.(
        attachmentsList.length,
        meetingId,
        itemId,
        sessionId,
        taskId,
      );
    }
    prevCountRef.current = attachmentsList.length;
  }, [attachmentsList.length, meetingId, itemId, open, sessionId, taskId]);

  // Separate files and hyperlinks
  const { files, hyperlinks } = useMemo(() => {
    const sortedList = [...attachmentsList].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
    return {
      files: sortedList.filter((a) => !a.external),
      hyperlinks: sortedList.filter((a) => a.external),
    };
  }, [attachmentsList]);

  // Check if user can delete an attachment
  const canDeleteAttachment = useCallback(
    (attachment: Attachment): boolean => {
      if (!allowModifications) return false;
      if (
        appContext.state.invoker?.role === UserRole.Actor &&
        attachment.creatorEmail !== appContext.state.invoker?.email
      ) {
        return false;
      }
      return true;
    },
    [allowModifications, appContext.state.invoker],
  );

  // Handle file upload
  const handleFilesSelected = useCallback(
    async (selectedFiles: File[]) => {
      // In pending mode, store files locally without uploading
      if (isPendingMode) {
        const newPendingAttachments: PendingAttachment[] = selectedFiles.map((file) => ({
          attachment: {
            id: randomId(),
            url: URL.createObjectURL(file), // Local URL for preview
            name: file.name,
            displayName: file.name,
          },
          file,
        }));
        
        const updatedPending = [...(pendingAttachments || []), ...newPendingAttachments];
        onPendingAttachmentsChangeRef.current?.(updatedPending);
        return;
      }
      
      // Must have uploadPrefix AND at least one valid context:
      // - meetingId only (meeting attachments)
      // - meetingId + itemId (task attachments from meeting)
      // - itemId only (quicktask attachments)
      // - sessionId (session attachments)
      // - sessionId + taskId (coaching task attachments)
      const hasValidContext = meetingId || itemId || sessionId;
      if (!uploadPrefix || !hasValidContext) return;

      setIsUploading(true);
      setUploadProgress(0);

      const progressPerFile = 100 / selectedFiles.length;

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        try {
          // Upload file to Azure Blob Storage
          const blob = await createBlobInContainer(
            uploadPrefix,
            file,
            meetingId,
            itemId,
            sessionId,
            taskId,
          );
          // Save attachment record to backend and get the server-returned object
          const savedAttachment = await createAttachment(
            blob,
            meetingId,
            itemId,
            sessionId,
            taskId,
          );
          // Merge local blob data with server response to ensure displayName is always set
          const mergedAttachment = {
            ...blob,
            ...savedAttachment,
            displayName: savedAttachment.displayName || blob.displayName,
          };
          // Add SAS token to the URL so the file can be downloaded immediately
          const [decorated] = await decorateBlobs([mergedAttachment]);
          // Add the attachment to the list
          setLoadedAttachments((prev) => [...prev, decorated]);
          setUploadProgress((i + 1) * progressPerFile);
        } catch (error) {
          console.error(`Error uploading file "${file.name}":`, error);
        }
      }

      if (uploadResetTimeoutRef.current) {
        clearTimeout(uploadResetTimeoutRef.current);
      }
      uploadResetTimeoutRef.current = setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        uploadResetTimeoutRef.current = null;
      }, 300);
    },
    [meetingId, itemId, sessionId, taskId, uploadPrefix, isPendingMode, pendingAttachments],
  );

  // Handle hyperlink addition
  const handleAddHyperlink = useCallback(
    async (name: string, url: string) => {
      const attachment: Attachment = {
        id: randomId(),
        url,
        name: name || url,
        displayName: name || url,
        external: true,
      };

      // In pending mode, store hyperlink locally without saving to backend
      if (isPendingMode) {
        const newPendingAttachment: PendingAttachment = {
          attachment,
          // No file for hyperlinks
        };
        
        const updatedPending = [...(pendingAttachments || []), newPendingAttachment];
        onPendingAttachmentsChangeRef.current?.(updatedPending);
        return;
      }

      // Must have at least one valid context
      const hasValidContext = meetingId || itemId || sessionId;
      if (!hasValidContext) return;

      try {
        const created = await createAttachment(
          attachment,
          meetingId,
          itemId,
          sessionId,
          taskId,
        );
        setLoadedAttachments((prev) => [
          ...prev,
          {
            ...created,
            external: true,
            displayName: created.displayName || created.name || url,
          },
        ]);
      } catch (error) {
        console.error("Error adding hyperlink:", error);
      }
    },
    [meetingId, itemId, sessionId, taskId, isPendingMode, pendingAttachments],
  );

  // Handle delete click
  const handleDeleteClick = useCallback((attachment: Attachment) => {
    setAttachmentToDelete(attachment);
  }, []);

  // Confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (!attachmentToDelete) return;
    
    // In pending mode, just remove from local list
    if (isPendingMode) {
      const updatedPending = (pendingAttachments || []).filter(
        p => p.attachment.id !== attachmentToDelete.id
      );
      // Revoke object URL if it was a file
      const pendingItem = pendingAttachments?.find(p => p.attachment.id === attachmentToDelete.id);
      if (pendingItem?.file && pendingItem.attachment.url.startsWith('blob:')) {
        URL.revokeObjectURL(pendingItem.attachment.url);
      }
      onPendingAttachmentsChangeRef.current?.(updatedPending);
      setAttachmentToDelete(null);
      return;
    }
    
    // Must have at least one valid context for non-pending mode
    const hasValidContext = meetingId || itemId || sessionId;
    if (!hasValidContext) return;

    try {
      if (!attachmentToDelete.external) {
        await deleteBlobFromContainer(
          meetingId,
          attachmentToDelete.name,
          itemId,
          sessionId,
          taskId,
        );
      }
      await deleteAttachment(
        attachmentToDelete,
        meetingId,
        itemId,
        sessionId,
        taskId,
      );
      setLoadedAttachments((prev) =>
        prev.filter((a) => a.id !== attachmentToDelete.id),
      );
    } catch (error) {
      console.error("Error deleting attachment:", error);
    }

    setAttachmentToDelete(null);
  }, [attachmentToDelete, meetingId, itemId, sessionId, taskId, isPendingMode, pendingAttachments]);

  // Cancel delete
  const handleCancelDelete = useCallback(() => {
    setAttachmentToDelete(null);
  }, []);

  // Handle close
  const handleClose = useCallback(
    (event?: object, reason?: string) => {
      if (reason === "backdropClick") return;
      onClose?.();
    },
    [onClose],
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullScreen={false}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        slotProps={{
          transition: { onEntered: () => setDialogEntered(true) },
          paper: {
            sx: {
              borderRadius: { xs: "0", sm: "8px" },
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              maxHeight: { xs: "100vh", sm: "90vh" },
              height: { xs: "100vh", sm: "auto" },
              width: { xs: "100vw", sm: "90vw", md: "1170px" },
              maxWidth: { xs: "100vw", sm: "95vw" },
              margin: { xs: 0, sm: 2 },
              backgroundColor: "#FFFFFF",
            },
          },
        }}
      >
        <Box id="draggable-dialog-title" sx={{ cursor: "grab" }}>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 2,
              px: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DragIndicatorIcon
                sx={{
                  color: "#98A2B3",
                  fontSize: 20,
                  display: { xs: "none", sm: "block" },
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                  fontWeight: 600,
                  color: "#1A1A2E",
                }}
              >
                {t("attachment_modal.title", "Upload files and hyperlinks")}
              </Typography>
            </Box>
            <IconButton
              onClick={() => handleClose()}
              size="small"
              sx={{
                color: "#666",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </DialogTitle>
        </Box>

        <Divider sx={{ borderColor: "#E5E7EB" }} />

        <DialogContent
          sx={{ pt: 3, px: { xs: 2, sm: 3 }, pb: 2, overflowY: "auto" }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 3, md: 4 },
                alignItems: "stretch",
              }}
            >
              {/* Left Column - Files */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: { xs: "100%", md: "auto" },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "#374151",
                    mb: 1.5,
                  }}
                >
                  {t("attachment_modal.upload_files", "Upload your files")}
                </Typography>

                <FileDropZone
                  disabled={!allowModifications}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                  onFilesSelected={handleFilesSelected}
                />

                {/* Uploaded Files List */}
                {files.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      maxHeight: "250px",
                      overflowY: "auto",
                      flexGrow: 1,
                    }}
                  >
                    {files.map((file) => (
                      <FileListItem
                        key={file.id}
                        attachment={file}
                        allowModifications={allowModifications}
                        canDelete={canDeleteAttachment(file)}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Vertical Divider - Hidden on mobile */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  borderColor: "#E5E7EB",
                  display: { xs: "none", md: "block" },
                }}
              />

              {/* Horizontal Divider - Shown on mobile only */}
              <Divider
                sx={{
                  borderColor: "#E5E7EB",
                  display: { xs: "block", md: "none" },
                  width: "100%",
                }}
              />

              {/* Right Column - Hyperlinks */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: { xs: "100%", md: "auto" },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "#374151",
                    mb: 1.5,
                  }}
                >
                  {t("attachment_modal.add_hyperlinks", "Add hyperlinks")}
                </Typography>

                {/* Wrapper to match FileDropZone height */}
                <Box
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    p: { xs: 2, sm: 2.5 },
                    mb: 2,
                    backgroundColor: "#FAFAFA",
                    minHeight: { xs: "120px", sm: "150px" },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <HyperlinkForm
                    disabled={!allowModifications}
                    onAddHyperlink={handleAddHyperlink}
                  />
                </Box>

                {/* Hyperlinks List */}
                {hyperlinks.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      maxHeight: "250px",
                      overflowY: "auto",
                      flexGrow: 1,
                    }}
                  >
                    {hyperlinks.map((link) => (
                      <HyperlinkListItem
                        key={link.id}
                        attachment={link}
                        allowModifications={allowModifications}
                        canDelete={canDeleteAttachment(link)}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <Divider sx={{ borderColor: "#E5E7EB" }} />

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            flexDirection: { xs: "column-reverse", sm: "row" },
            "& > button": {
              width: { xs: "100%", sm: "auto" },
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={() => handleClose()}
            sx={{
              borderColor: "#D0D5DD",
              color: "#374151",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              borderRadius: "6px",
              px: 3,
              py: { xs: 1.5, sm: 1 },
              "&:hover": {
                borderColor: "#98A2B3",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleClose()}
            sx={{
              backgroundColor: "#242C63",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              borderRadius: "6px",
              boxShadow: "none",
              px: 3,
              py: { xs: 1.5, sm: 1 },
              "&:hover": {
                backgroundColor: "#1E2554",
                boxShadow: "none",
              },
            }}
          >
            {t("attachment_modal.save_close", "Save & Close")}
          </Button>
        </DialogActions>
      </Dialog>

      {attachmentToDelete && (
        <ConfirmationPanel
          showModal={!!attachmentToDelete}
          confirmModal={handleConfirmDelete}
          hideModal={handleCancelDelete}
          entry={attachmentToDelete}
          type={"attachment"}
          action="delete"
        />
      )}
    </>
  );
};
